/* Original JS Speed Shop search component. No dependency on Enterprise or Gearx search classes. */
(() => {
  if (customElements.get('jss-header-search')) return;

  class JssHeaderSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.inline = this.querySelector('[data-jss-inline]');
      this.dialog = this.querySelector('[data-jss-dialog]');
      this.input = this.querySelector('[data-jss-input]');
      this.results = this.querySelector('[data-jss-results]');
      this.status = this.querySelector('[data-jss-status]');
      this.clearButton = this.querySelector('[data-jss-clear]');
      this.viewAll = this.querySelector('[data-jss-view-all]');
      this.empty = this.querySelector('[data-jss-empty]');
      this.cache = new Map();
      this.sequence = 0;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phrases = (this.dataset.phrases || '').split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
      this.supportsDialog = typeof this.dialog.showModal === 'function';
      const on = (target, name, fn, options = {}) => target.addEventListener(name, fn, { ...options, signal: this.events.signal });

      if (this.supportsDialog) {
        this.inline.setAttribute('role', 'combobox');
        this.inline.setAttribute('aria-haspopup', 'dialog');
        this.inline.setAttribute('aria-controls', this.dialog.id);
        this.inline.setAttribute('aria-expanded', 'false');
        on(this.inline, 'focus', () => { if (!this.returningFocus) this.open(); });
        on(this.inline, 'click', () => this.open());
      }
      on(this.inline, 'blur', () => this.startAnimation());
      on(this.inline, 'input', () => this.stopAnimation());
      on(this.input, 'input', event => { if (!event.isComposing) this.search(); });
      on(this.input, 'compositionend', () => this.search());
      on(this.input, 'keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
        if (event.key === 'ArrowDown') {
          const first = this.visibleLinks()[0];
          if (first) { event.preventDefault(); first.focus(); }
        }
      });
      on(this.clearButton, 'click', () => {
        this.input.value = '';
        this.input.focus();
        this.search();
      });
      on(this.querySelector('[data-jss-close]'), 'click', () => this.close());
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.dialog, 'close', () => this.afterClose());
      on(this.dialog, 'click', event => {
        // Backdrop events target the dialog; ignore clicks inside its bounding box.
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
      });
      on(this.results, 'click', event => {
        const tab = event.target.closest('[data-jss-tab]');
        if (tab) this.selectTab(tab.dataset.jssTab);
        const quick = event.target.closest('[data-jss-query]');
        if (quick && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
          event.preventDefault(); this.input.value = quick.dataset.jssQuery; this.input.focus(); this.search();
        }
      });
      on(this.results, 'keydown', event => this.navigateResults(event));
      this.querySelectorAll('form').forEach(form => on(form, 'submit', event => {
        const field = form.querySelector('input[name="q"]');
        if (!field.value.trim()) { event.preventDefault(); field.focus(); }
      }));
      on(window, 'resize', () => { if (this.dialog.open) this.position(); });
      if (window.visualViewport) {
        on(window.visualViewport, 'resize', () => { if (this.dialog.open) this.position(); });
        on(window.visualViewport, 'scroll', () => { if (this.dialog.open) this.position(); });
      }
      on(this.motion, 'change', () => this.startAnimation());
      on(document, 'visibilitychange', () => document.hidden ? this.stopAnimation() : this.startAnimation());
      this.bindMenu(on);
      this.startAnimation();
    }

    disconnectedCallback() {
      this.stopAnimation();
      this.invalidate();
      if (this.dialog?.open) this.close(false);
      this.menuObserver?.disconnect();
      this.events?.abort();
      this.events = null;
    }

    bindMenu(on) {
      const header = this.closest('.jss-header');
      const menu = header?.querySelector('.jss-header__nav');
      const button = header?.querySelector('.jss-header__menu-button');
      if (!menu || !button) return;
      const sync = () => button.setAttribute('aria-expanded', String(menu.classList.contains('open-header-menu')));
      this.menuObserver = new MutationObserver(sync);
      this.menuObserver.observe(menu, { attributes: true, attributeFilter: ['class'] });
      const closer = menu.querySelector('.close-hd-mobile__button');
      if (closer) {
        closer.setAttribute('role', 'button'); closer.setAttribute('tabindex', '0'); closer.setAttribute('aria-label', 'Close menu');
        on(closer, 'keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); closer.click(); button.focus(); } });
      }
      on(menu, 'keydown', event => {
        if (event.key === 'Escape') { menu.classList.remove('open-header-menu'); button.focus(); }
        const topLink = event.target.closest('.horizontal-list > li.level0 > a');
        if (topLink && window.innerWidth < 1024 && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault(); topLink.parentElement.click();
        }
      });
      sync();
    }

    open() {
      if (!this.supportsDialog || this.dialog.open || this.returningFocus) return;
      this.stopAnimation();
      this.anchor = this.inline.closest('form').getBoundingClientRect();
      this.input.value = this.inline.value;
      this.activeTab = 'products';
      this.inline.setAttribute('aria-expanded', 'true');
      this.closest('.jss-header')?.querySelectorAll('.is-open, .open-header-menu').forEach(el => el.classList.remove('is-open', 'open-header-menu'));
      this.position();
      this.dialog.showModal();
      document.documentElement.classList.add('jss-search-open');
      this.input.focus({ preventScroll: true });
      this.search(true);
    }

    position() {
      const vv = window.visualViewport;
      const width = vv?.width || window.innerWidth;
      const height = vv?.height || window.innerHeight;
      const offsetY = vv?.offsetTop || 0;
      const offsetX = vv?.offsetLeft || 0;
      const mobile = width < 768;
      const dialogWidth = mobile ? width - 16 : Math.min(1100, width - 48);
      const left = mobile ? offsetX + 8 : offsetX + Math.min(Math.max(24, this.anchor.left - 12), width - dialogWidth - 24);
      const top = offsetY + (height < 550 ? 8 : Math.min(Math.max(12, this.anchor.top - 12), height * .18));
      this.dialog.style.setProperty('--jss-dialog-left', `${left}px`);
      this.dialog.style.setProperty('--jss-dialog-top', `${top}px`);
      this.dialog.style.setProperty('--jss-dialog-width', `${dialogWidth}px`);
      this.dialog.style.setProperty('--jss-dialog-height', `${Math.max(150, height - (top - offsetY) - 12)}px`);
    }

    close(restoreFocus = true) {
      if (!this.dialog.open) return;
      this.returningFocus = true;
      this.restoreFocus = restoreFocus;
      this.inline.value = this.input.value;
      this.invalidate();
      document.documentElement.classList.remove('jss-search-open');
      this.inline.setAttribute('aria-expanded', 'false');
      this.dialog.close();
    }

    afterClose() {
      this.inline.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('jss-search-open');
      if (this.restoreFocus !== false && this.isConnected) this.inline.focus({ preventScroll: true });
      this.returningFocus = false;
      this.startAnimation();
    }

    invalidate() {
      this.sequence++;
      clearTimeout(this.debounce);
      this.request?.abort();
      clearTimeout(this.requestTimeout);
    }

    search(immediate = false) {
      this.invalidate();
      const query = this.input.value.trim();
      this.inline.value = this.input.value;
      this.clearButton.hidden = !this.input.value;
      this.results.setAttribute('aria-busy', 'false');
      this.status.textContent = '';
      this.viewAll.hidden = !query;
      const url = new URL(this.input.form.action, window.location.origin);
      url.searchParams.set('q', query); url.searchParams.set('options[prefix]', 'last');
      this.viewAll.href = url.href;
      this.viewAll.replaceChildren(document.createTextNode(`View all results for “${query}”`));
      const arrow = document.createElement('span'); arrow.textContent = '→'; arrow.setAttribute('aria-hidden', 'true'); this.viewAll.append(arrow);
      this.results.replaceChildren();
      if (!query) { this.results.append(this.empty.content.cloneNode(true)); return; }
      if (this.dataset.predictive === 'false') { this.status.textContent = 'Press Enter or choose View all results to search.'; return; }
      this.status.textContent = 'Searching…';
      this.results.setAttribute('aria-busy', 'true');
      const sequence = this.sequence;
      this.debounce = setTimeout(() => this.fetchResults(query, sequence), immediate ? 0 : 250);
    }

    async fetchResults(query, sequence) {
      if (this.cache.has(query)) { this.render(this.cache.get(query), sequence); return; }
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      this.requestTimeout = timeout;
      try {
        const url = new URL(this.dataset.endpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'jss-predictive-search');
        url.searchParams.set('resources[type]', 'product,query,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search request failed');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('[data-jss-response]');
        if (!content) throw new Error('Search response unavailable');
        if (sequence !== this.sequence || !this.dialog.open) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, content.outerHTML);
        this.render(content.outerHTML, sequence);
      } catch (error) {
        if (sequence !== this.sequence || !this.dialog.open) return;
        this.results.setAttribute('aria-busy', 'false');
        this.status.textContent = 'Live results are unavailable. Press Enter or choose View all results to continue.';
      } finally { clearTimeout(timeout); }
    }

    render(html, sequence) {
      if (sequence !== this.sequence || !this.dialog.open) return;
      this.results.innerHTML = html;
      this.results.setAttribute('aria-busy', 'false');
      this.results.scrollTop = 0;
      const count = Number(this.results.querySelector('[data-count]')?.dataset.count || 0);
      this.status.textContent = count ? `${count} suggestions and results. Choose a category below.` : 'No matching suggestions.';
      const tabs = [...this.results.querySelectorAll('[data-jss-tab]')];
      tabs.forEach(tab => {
        const group = tab.dataset.jssTab;
        const panel = this.results.querySelector(`[data-jss-panel="${group}"]`);
        tab.id = `${this.dialog.id}-tab-${group}`;
        panel.id = `${this.dialog.id}-panel-${group}`;
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', tab.id);
      });
      this.selectTab(tabs.some(t => t.dataset.jssTab === this.activeTab) ? this.activeTab : tabs[0]?.dataset.jssTab);
    }

    selectTab(group) {
      this.activeTab = group;
      this.results.querySelectorAll('[data-jss-tab]').forEach(tab => {
        const active = tab.dataset.jssTab === group;
        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
      });
      this.results.querySelectorAll('[data-jss-panel]').forEach(panel => { panel.hidden = panel.dataset.jssPanel !== group; });
    }

    visibleLinks() {
      return [...this.results.querySelectorAll('a[href]')].filter(link => !link.closest('[hidden]'));
    }

    navigateResults(event) {
      const tab = event.target.closest('[data-jss-tab]');
      if (tab && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
        event.preventDefault();
        const tabs = [...this.results.querySelectorAll('[data-jss-tab]')];
        let next = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        const target = tabs[(next + tabs.length) % tabs.length];
        this.selectTab(target.dataset.jssTab); target.focus(); target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else if (event.target.matches('a') && ['ArrowUp','ArrowDown'].includes(event.key)) {
        event.preventDefault();
        const links = this.visibleLinks();
        const next = links.indexOf(event.target) + (event.key === 'ArrowDown' ? 1 : -1);
        if (next < 0) this.input.focus(); else (links[next] || this.viewAll).focus();
      }
    }

    stopAnimation() { clearTimeout(this.animation); }

    startAnimation() {
      this.stopAnimation();
      if (!this.inline) return;
      this.inline.placeholder = this.dataset.fallback || 'Search for parts or part number';
      if (this.dataset.animate === 'false' || this.motion.matches || document.hidden || !this.phrases.length || this.inline.value || this.dialog.open || document.activeElement === this.inline) return;
      let index = 0, word = this.phrases[0], length = word.length, deleting = true;
      this.inline.placeholder = `Search for ${word}`;
      const tick = () => {
        if (!this.isConnected || this.inline.value || this.dialog.open || document.activeElement === this.inline || this.motion.matches || document.hidden) return;
        word = this.phrases[index];
        length += deleting ? -1 : 1;
        this.inline.placeholder = `Search for ${word.slice(0, length)}`;
        let delay = deleting ? 60 : 100;
        if (deleting && length === 0) { deleting = false; index = (index + 1) % this.phrases.length; delay = 500; }
        else if (!deleting && length === word.length) { deleting = true; delay = 2400; }
        this.animation = setTimeout(tick, delay);
      };
      this.animation = setTimeout(tick, 2000);
    }
  }
  customElements.define('jss-header-search', JssHeaderSearch);
})();
